import type { EventBus } from '../core/EventBus';
import type { TravelConfig } from '../config/TravelConfig';
import type { FogState } from '../world/FogState';
import type { TravelLineRenderer } from '../objects/TravelLineRenderer';
import type { FleetRenderer } from '../objects/FleetRenderer';
import type { FleetState } from './FleetState';
import type { FleetIndicator } from '../objects/FleetIndicator';
import { TravelState } from './TravelState';
import { TravelAnimator } from './TravelAnimator';
import { TravelMode, TravelPhase } from './TravelTypes';

/**
 * Holt verpasste Reisezeit nach wenn der Tab im Hintergrund war.
 * Wird von TravelSystem genutzt, reagiert auf 'engine:catch-up'.
 */
export class TravelCatchUp {
  private _events!: EventBus;
  private readonly _hopEvt = { q: 0, r: 0 };

  constructor(
    private _state: TravelState,
    private _animator: TravelAnimator,
    private _config: TravelConfig,
    private _fogState: FogState | null,
    private _line: TravelLineRenderer,
    private _fleetRenderer: FleetRenderer,
    private _fleet: FleetState,
    private _indicator: FleetIndicator,
  ) {}

  init(events: EventBus): void {
    this._events = events;
    events.on<{ seconds: number }>('engine:catch-up', (d) => this._catchUp(d.seconds));
  }

  private _catchUp(seconds: number): void {
    this._line.advanceTime(seconds);
    if (this._state.phase !== TravelPhase.Traveling || !this._animator.active) return;

    this._state.elapsedSeconds += seconds;
    const fh = this._config.flightHeight;
    let r = this._animator.update(seconds);

    for (let i = 0; i < 500; i++) {
      if (r.hopCompleted) {
        this._state.currentHopIdx++;
        this._revealAtHex(r.hexQ, r.hexR);
        this._hopEvt.q = r.hexQ;
        this._hopEvt.r = r.hexR;
        this._events.emit('travel:hop', this._hopEvt);
      }
      if (r.finished) {
        this._fleet.update(r.worldX, fh, r.worldZ, r.dirX, r.dirZ);
        this._fleetRenderer.updateFormation();
        this._state.arrive();
        this._events.emit('travel:phase-changed', { phase: this._state.phase });
        this._state.ackPhaseChange();
        this._events.emit('travel:arrived', null);
        this._animator.stop();
        this._setFleetActive(false);
        return;
      }
      if (!r.hopCompleted) break;
      r = this._animator.update(0);
    }

    this._fleet.update(r.worldX, fh, r.worldZ, r.dirX, r.dirZ);
    this._fleetRenderer.updateFormation();
  }

  private _revealAtHex(q: number, r: number): void {
    if (!this._fogState) return;
    const m = this._state.mode;
    const radius = m === TravelMode.Reveal ? this._config.reveal.revealRadius
      : m === TravelMode.Warp ? this._config.warp.revealRadius
      : this._config.normal.revealRadius;
    if (radius < 0) return;
    this._fogState.addReveal(q, r, radius);
    this._events.emit('fog:reveals-changed', null);
  }

  private _setFleetActive(active: boolean): void {
    this._fleetRenderer.setVisible(active);
    this._fleet.active = active;
    this._indicator.setVisible(active);
    if (!active) this._events.emit('fleet:exit-flightview', null);
  }
}
